import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';
import Joi from 'joi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { useCreateMassNotificationMutation } from 'api/main';
import { userSelectors } from 'reducers';
import { useMatchAtLeastOneRole } from 'utils/hooks';
import { validateSchema } from 'utils/helpers';
import { RoleNames, quillFormats } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { DocumentUpload } from 'containers/tunnel/common';
import { ActionButton, CustomDialogTitle, CustomTextField, ErrorMessage } from 'components';
import { Dialog, DialogActions, DialogContent, Typography } from '@mui/material';

const defaultMessage = '';
const schema = {
    subject: Joi.string().required().messages({ 'string.empty': texts.VEUILLES_SAISIR_UN_SUJET }),
    editorValue: Joi.string()
        .required()
        .invalid('<p><br></p>', defaultMessage)
        .messages({ 'string.base': texts.VEUILLEZ_SAISIR_LE_CONTENU }),
};

const MessageDialog = ({ filters: { enrichWith, ...filters }, open, onClose }) => {
    const internalUser = useSelector(userSelectors.getInternalUser);
    const distributorLogoURL = useSelector(userSelectors.getDistributorLogoUrl);
    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
    ]);

    const [form, setForm] = useState({
        subject: '',
        editorValue: defaultMessage + '',
        attachment: '',
    });

    const [createMassNotification, { isLoading, isSuccess }] = useCreateMassNotificationMutation();

    const [errors, setErrors] = useState({
        subject: '',
        editorValue: '',
    });

    const sender = useMemo(() => {
        if (userIsDistributor) {
            return internalUser?.distributorCompany?.corporateName || '';
        }
        return texts.PEQAN_TEAM;
    }, [userIsDistributor, internalUser]);

    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({
                ...prevState,
                [key]: key === 'editorValue' ? e : e.target.value,
            }));
        },
        []
    );

    const handleFileChange = useCallback(e => {
        setForm(prevState => ({ ...prevState, attachment: e.target.files[0] }));
    }, []);

    const handleremoveFile = useCallback(e => {
        setForm(prevState => ({ ...prevState, attachment: '' }));
    }, []);

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        return valid;
    }, [form]);

    const handleSendNotification = useCallback(() => {
        if (formIsValid()) {
            createMassNotification({
                subject: form.subject,
                messageContent: form.editorValue,
                senderName: sender,
                userIsDistributor,
                distributorLogoURL,
                attachment: form.attachment,
                filters,
            });
        }
    }, [
        form,
        createMassNotification,
        distributorLogoURL,
        filters,
        formIsValid,
        sender,
        userIsDistributor,
    ]);

    useEffect(() => {
        if (isSuccess) {
            onClose();
        }
    }, [isSuccess, onClose]);

    useEffect(() => {
        if (!open) {
            setForm({
                subject: '',
                editorValue: defaultMessage + '',
                attachment: '',
            });
        }
    }, [open]);

    return (
        <Dialog open={open} maxWidth="md" fullWidth>
            <CustomDialogTitle
                title={texts.ENVOYER_UN_MESSAGE}
                handleClose={isLoading ? null : onClose}
            />
            <DialogContent sx={theme => ({ padding: theme.spacing(2, 6) })}>
                <CustomTextField
                    fullWidth
                    id="subject"
                    label={texts.SUBJECT}
                    value={form.subject}
                    onChange={handleChange('subject')}
                    error={errors.subject}
                />
                <Typography gutterBottom align="left" variant="body2" display="block">
                    {texts.VOTRE_MESSAGE}
                </Typography>
                <ReactQuill
                    value={form.editorValue}
                    onChange={handleChange('editorValue')}
                    formats={quillFormats}
                />
                <DocumentUpload
                    id="attachment-file"
                    label={texts.ATTACHMENT_FILE}
                    selectedFiles={[form.attachment]}
                    handleFileChange={handleFileChange}
                    removeFile={handleremoveFile}
                />
            </DialogContent>
            {errors.editorValue && (
                <ErrorMessage
                    gutterBottom
                    variant="caption"
                    align="center"
                    message={texts.VEUILLEZ_SAISIR_LE_CONTENU}
                />
            )}
            <DialogActions>
                <ActionButton
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={handleSendNotification}
                >
                    {texts.ENVOYER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

MessageDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    filters: PropTypes.object.isRequired,
};

export default MessageDialog;
